// ============================================================================
// <PublicPage> — the scroll container for every route that renders OUTSIDE
// <NavShell>. 2026-09-11.
//
// WHY THIS EXISTS. The app is a fixed shell (see the comment above the rule in
// src/index.css): `html, body, #root { height: 100dvh }` and
// `body { overflow: hidden }`, so #root is exactly one viewport tall and the
// body never scrolls. Inside <NavShell> that is correct and deliberate — the
// header and the bottom tab bar are flex children of #root and only <main>
// scrolls. A route that renders directly under #root has no <main>: its
// content overflows a box exactly one viewport tall, `overflow: hidden`
// propagates from body to the viewport, and everything below the fold is
// clipped with no scroller able to reach it.
//
// Six public routes carried that defect at once — /apply/:token, /onboard/:token,
// /sign/:token, /hours/:token, /clock and the fleet tag resolver. Every one of
// them is a surface people OUTSIDE the company use, and two of them (the
// sub-onboarding portal and the e-sign signer page) had never been reported,
// which is what an unreachable submit button looks like from the outside: not a
// complaint, just an unfinished form.
//
// ⛔ THE GLOBAL SHELL CSS IS NOT THE BUG AND DOES NOT MOVE. The fix is that an
// unshelled route owns its own scroll container, exactly as <main> does for the
// shelled ones.
//
// HOW: fill #root (`h-full`, which resolves because #root has an explicit
// height) and scroll internally. ⛔ Never `min-h-screen` — that makes the box
// AT LEAST a viewport tall inside a parent that clips at exactly one viewport,
// which is the defect this replaces. `overscroll-contain` keeps the page from
// chaining its scroll (and iOS pull-to-refresh) out to a body that declares
// `overscroll-behavior: none`.
//
// Composes with <ForceLight> rather than replacing it: PublicPage owns the
// scroll, ForceLight owns the theme. PublicPage goes OUTSIDE.
// ============================================================================

import type { ReactNode } from 'react';

export function PublicPage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-public-page=""
      className={['h-full overflow-y-auto overscroll-contain', className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}

/**
 * The element that actually scrolls the current page.
 *
 * ⛔ `window.scrollTo` HAS NEVER WORKED IN THIS APP. `body { overflow: hidden }`
 * means the viewport does not scroll anywhere — inside the shell it is <main>
 * that scrolls, and outside it, <PublicPage>. Nine call sites were quietly
 * scrolling nothing, including every "back to the top" after a step in the
 * e-sign signer walk, which is a public route.
 */
export function pageScroller(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>('[data-public-page]') ??
    document.querySelector<HTMLElement>('main')
  );
}

/** Scroll the page's real scroll container. Falls back to the window so a
 *  surface rendered somewhere neither container exists still behaves. */
export function scrollPageTo(top: number, behavior?: ScrollBehavior): void {
  const el = pageScroller();
  if (el) el.scrollTo({ top, behavior });
  else window.scrollTo({ top, behavior });
}

export function scrollPageTop(behavior?: ScrollBehavior): void {
  scrollPageTo(0, behavior);
}
